import React, { useMemo, useState } from "react";

import {
  TFunction,
  calculateFieldContributionStatus,
  checkFeatureFlag,
  formatNumberRange,
  formatNumericCurrency,
  getEnvironment,
  isSingleFieldOutcomes,
  someOutcomeHasErrorOfType,
  useGetCarbonProgramByFarmId,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldEvaluationMetricCorrections,
  useInventoryImpact,
  useTranslation,
} from "utils";

import {
  CARBON_RUNNER_ERROR_TYPE,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  IMPACT_PANEL_CALCULATION_TYPE,
  SingleFieldOutcomes,
  VIEW_TYPE,
} from "models";

import { Col, Flex, Icon, IconLinkButton, LoadingOverlay, Option, Select, Tooltip } from "components";

import { SubmissionSuccessModalContent } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { EvaluationMetricsPanel } from "pages/app/field-timeline/components/EvaluationMetricsPanel";
import { ImpactPanelErrorView } from "pages/app/field-timeline/components/ImpactPanelErrorView";
import { MissingDataGuidance } from "pages/app/field-timeline/components/MissingDataGuidance";
import { OutcomeBasedImpact } from "pages/app/field-timeline/components/OutcomeBasedImpact";
import { PracticeBasedImpact } from "pages/app/field-timeline/components/PracticeBasedImpact";
import { calculateImpactPanelBodyHeight } from "pages/app/field-timeline/utils/TimelineUtils";

type InventoryImpactPanelProps = {
  carbonProgramOutcomes?: CarbonProgramOutcomesResponse;
  fieldSizes: Record<string, number>;
  progress: number;
  reportingYearFromProps: number;
  shouldShowSubmissionSuccessModalContent?: boolean;
  hidePayouts?: boolean;
};

const FullPaymentBadge = ({ t }: { t: TFunction }) => (
  <Flex className="maximum-payment-badge" data-testid="full-payment-badge" align="center" gap="0.5rem">
    <Icon iconName="klim/medal" color="white" />
    <span className="paragraph-s-bold white-text">{t("impactPanel.inventory.fullPaymentBadge")}</span>
  </Flex>
);

const EmptyDataView = ({ t }: { t: TFunction }) => (
  <div data-testid="field-timeline-result-card-empty" className="field-timeline-result-card">
    <div className="field-timeline-result-card container empty">
      <Flex vertical={true} align="center" justify="center" style={{ height: "100%" }}>
        <Icon dataTestId="field-timeline-result-card-empty-payout-funding-icon" className="margin-bottom-100" iconName="klim/funding" size="xl" />
        <p data-testid="field-timeline-result-card-empty-title" className="field-timeline-result-card-empty-title">
          {t("impactPanel.emptyPayoutTitle")}
        </p>
        <span data-testid="field-timeline-result-card-empty-payout-description" className="field-timeline-result-card-empty-payout-description">
          {t("impactPanel.emptyPayoutDescription")}
        </span>
      </Flex>
    </div>
  </div>
);

export const InventoryImpactPanel = ({
  carbonProgramOutcomes,
  fieldSizes,
  progress,
  hidePayouts,
  shouldShowSubmissionSuccessModalContent = false,
  reportingYearFromProps,
}: InventoryImpactPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const env = getEnvironment();
  const areEvaluationMetricsEnabled = env.production ? checkFeatureFlag("evaluation-metrics") : true;
  const shouldHidePanel = !areEvaluationMetricsEnabled && hidePayouts;

  const [selectedToggleOption, setSelectedToggleOption] = useState<IMPACT_PANEL_CALCULATION_TYPE>(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
  const [selectedPanel, setSelectedPanel] = useState<"payout" | EVALUATION_METRICS>(hidePayouts ? EVALUATION_METRICS.HUMUS : "payout");
  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomes ?? {}).filter(isSingleFieldOutcomes);

  const panelMetrics = areEvaluationMetricsEnabled ? ["payout", ...Object.values(EVALUATION_METRICS)] : ["payout"];

  const { currentFarm } = useGetFarms();
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(currentFarm?.id || null);
  const reportingYear = activeFarmReportingYear ?? reportingYearFromProps;

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(currentFarm?.id ?? undefined);

  const { totalPayoutMin, totalPayoutMax, isMaxTotalPayoutReached, hideInventoryPayouts, maximumPotentialPayout, displayPerHectareOrTotalFarm } =
    useInventoryImpact(reportingYear, singleFieldOutcomes, currentFarm?.id ?? "", carbonProgram);

  // Calculate per hectare values when toggle is set to PER_HA
  const isPerHectare = selectedToggleOption === IMPACT_PANEL_CALCULATION_TYPE.PER_HA;

  const displayedTotalPayoutMin = displayPerHectareOrTotalFarm(totalPayoutMin, isPerHectare);
  const displayedTotalPayoutMax = displayPerHectareOrTotalFarm(totalPayoutMax, isPerHectare);
  const displayedMaximumPotentialPayout = displayPerHectareOrTotalFarm(maximumPotentialPayout, isPerHectare);

  const toggleOptions = [
    { label: t("impactPanel.total"), value: IMPACT_PANEL_CALCULATION_TYPE.TOTAL },
    { label: t("impactPanel.perHa"), value: IMPACT_PANEL_CALCULATION_TYPE.PER_HA },
  ];

  const { fieldsContributingToPayouts, totalFields } = calculateFieldContributionStatus(carbonProgramOutcomes ?? {}, reportingYear);

  const fieldIds = Object.keys(carbonProgramOutcomes ?? {});
  const { data: existingCorrectionsByFieldId } = useGetFieldEvaluationMetricCorrections({ fieldIds: areEvaluationMetricsEnabled ? fieldIds : [] });

  const bodyHeight = calculateImpactPanelBodyHeight();

  const hasErrorData = useMemo(
    () => someOutcomeHasErrorOfType(carbonProgramOutcomes ?? {}, CARBON_RUNNER_ERROR_TYPE.FOOTPRINT_SERVICE),
    [carbonProgramOutcomes]
  );

  return !shouldHidePanel ? (
    <Flex className="field-timeline-impact-panel container inventory-impact-panel" data-testid="field-timeline-impact-panel" vertical={true}>
      <div className="field-timeline-impact-panel body" style={{ height: `${bodyHeight}rem` }}>
        <Flex vertical>
          <Flex align="center" className="margin-bottom-100">
            <>
              {/* @ts-expect-error need to change */}
              <Select
                className="panel-select"
                dataTestId="panel-select"
                name="panelSelect"
                popupMatchSelectWidth={false}
                onChange={setSelectedPanel}
                defaultValue={selectedPanel}
                value={selectedPanel}
              >
                {panelMetrics.map((metric, i) => (
                  <Option
                    data-testid={`panel-select-option-${i}`}
                    value={metric}
                    key={i}
                    className="panel-select-option"
                    disabled={metric === "payout" && hidePayouts}
                  >
                    {metric === "payout" ? t("impactPanel.inventory.totalPayout") : t(`impactPanel.evaluationMetrics.${metric}`)}
                  </Option>
                ))}
              </Select>
              {selectedPanel === "payout" && (
                <Tooltip placement="top" title={t("impactPanel.inventory.totalPayoutTitle", { programName: currentFarm?.carbonProgram?.programName })}>
                  <IconLinkButton
                    className="payout-info-icon-button"
                    dataTestId="inventory-impact-panel-total-payout-info-icon-button"
                    theme="grey"
                    iconType="info"
                    size="small"
                  />
                </Tooltip>
              )}
            </>
          </Flex>
          {selectedPanel === "payout" && !hasErrorData && !hideInventoryPayouts && (
            <>
              <Flex justify="space-between" align="center">
                <>
                  <Flex justify="center" align="center" className="payout-selected-year-label">
                    {reportingYearFromProps}
                  </Flex>
                </>
                {/* @ts-expect-error need to change */}
                <Select
                  className="field-timeline-result-select"
                  dataTestId="field-timeline-result-select"
                  name="resultSelect"
                  popupMatchSelectWidth={false}
                  onChange={setSelectedToggleOption}
                  defaultValue={selectedToggleOption}
                  value={selectedToggleOption}
                >
                  {toggleOptions.map((option, i) => (
                    <Option
                      data-testid={`field-timeline-result-select-option-${i}`}
                      value={option.value}
                      key={i}
                      className="field-timeline-result-select-option"
                    >
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Flex>
              <span className="dotted-line-divider" />
            </>
          )}
        </Flex>
        {progress === 100 ? (
          <>
            {selectedPanel !== "payout" ? (
              <EvaluationMetricsPanel
                selectedMetric={selectedPanel}
                carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
                reportingYear={reportingYear}
                fieldSizes={fieldSizes}
                // right now, the inventory panel is only available for the AFV page
                viewType={VIEW_TYPE.ALL_FIELDS}
                existingCorrectionsByFieldId={existingCorrectionsByFieldId ?? {}}
                reportingPeriod={carbonProgram?.reportingPeriod}
              />
            ) : hasErrorData ? (
              <>
                <ImpactPanelErrorView />
              </>
            ) : hideInventoryPayouts ? (
              <>
                <EmptyDataView t={t} />
              </>
            ) : shouldShowSubmissionSuccessModalContent ? (
              <>
                <SubmissionSuccessModalContent
                  carbonProgramOutcomes={carbonProgramOutcomes ?? null}
                  farmId={currentFarm?.id ?? ""}
                  handleStartNextYear={() => {}} // 🚨 TODO : Follow up handle next year start
                  variant="panel"
                />
              </>
            ) : (
              <>
                {carbonProgram?.platformFeatures.fieldManagement?.outcomeBasedIncentives && (
                  <>
                    <Flex className="margin-top-50" justify="space-between">
                      {isMaxTotalPayoutReached ? (
                        <FullPaymentBadge t={t} />
                      ) : (
                        <Tooltip placement="top" title={t("impactPanel.inventory.totalPayouts")}>
                          <h4 data-testid="total-payout" className="green-text">
                            {formatNumberRange({ min: displayedTotalPayoutMin, max: displayedTotalPayoutMax, decimalPlaces: 0 })}
                          </h4>
                        </Tooltip>
                      )}
                      <Tooltip placement="top" title={t("impactPanel.inventory.totalPayoutPotential")}>
                        <h4 className={isMaxTotalPayoutReached ? "green-text" : "maximum-theoretical-payout"} data-testid="maximum-potential-payout">
                          {formatNumericCurrency(displayedMaximumPotentialPayout, { decimals: 0, currency: carbonProgram?.currency })}
                        </h4>
                      </Tooltip>
                    </Flex>
                    <OutcomeBasedImpact
                      isDividePerHectare={isPerHectare}
                      fieldSizes={fieldSizes}
                      reportingYear={reportingYear}
                      singleFieldOutcomes={singleFieldOutcomes}
                    />
                  </>
                )}
                <PracticeBasedImpact
                  singleFieldOutcomes={singleFieldOutcomes}
                  reportingYear={reportingYear}
                  isDividePerHectare={isPerHectare}
                  currency={carbonProgram?.currency}
                />
                <MissingDataGuidance fieldsContributingToPayouts={fieldsContributingToPayouts} totalFields={totalFields} />
              </>
            )}
          </>
        ) : (
          <Flex align="center" vertical>
            <LoadingOverlay>
              <Col className="loading-message">
                <p>{t("impactPanel.fieldsCalculated", { progress })}</p>
              </Col>
            </LoadingOverlay>
          </Flex>
        )}
      </div>
    </Flex>
  ) : null;
};
